import { COOKIE_STORAGE_KEY } from "./leadCapture";

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
  _fbq?: (...args: unknown[]) => void;
  plausible?: (name: string, options?: { props?: AnalyticsParams }) => void;
  ochroAnalyticsLoaded?: boolean;
  ochroGoogleConsentDefaulted?: boolean;
  ochroGoogleAnalyticsConfigured?: boolean;
  ochroGoogleAdsConfigured?: boolean;
  ochroMarketingLoaded?: boolean;
};

const SESSION_STORAGE_KEY = "ochro-analytics-session";
const ATTRIBUTION_STORAGE_KEY = "ochro-ad-attribution";
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";
const GOOGLE_ADS_CONVERSION_ID = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID || "";
const GOOGLE_ADS_LEAD_LABEL = import.meta.env.VITE_GOOGLE_ADS_LEAD_LABEL || "";
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || "";
const GOOGLE_TAG_ID = GA_MEASUREMENT_ID || GOOGLE_ADS_CONVERSION_ID;

type CookieConsent = {
  analytics?: unknown;
  marketing?: unknown;
};

type Attribution = {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
};

function getCookieConsent(): CookieConsent {
  try {
    return JSON.parse(localStorage.getItem(COOKIE_STORAGE_KEY) || "{}") as CookieConsent;
  } catch {
    return {};
  }
}

function hasAnalyticsConsent() {
  return getCookieConsent().analytics === true;
}

function hasMarketingConsent() {
  return getCookieConsent().marketing === true;
}

function createId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `ochro-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function sessionId() {
  try {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const next = createId();
    localStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return createId();
  }
}

function cleanUrl(value: string) {
  if (!value) return "";
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split("?")[0].split("#")[0];
  }
}

const emptyAttribution: Attribution = {
  source: "",
  medium: "",
  campaign: "",
  content: "",
  term: "",
  gclid: "",
  gbraid: "",
  wbraid: ""
};

function readStoredAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    return { ...emptyAttribution, ...parsed };
  } catch {
    return null;
  }
}

function writeStoredAttribution(attribution: Attribution) {
  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    /* tryb prywatny — pomiń zapis */
  }
}

function currentAttribution(): Attribution {
  const url = new URL(window.location.href);
  const fromUrl: Attribution = {
    source: url.searchParams.get("utm_source") || "",
    medium: url.searchParams.get("utm_medium") || "",
    campaign: url.searchParams.get("utm_campaign") || "",
    content: url.searchParams.get("utm_content") || "",
    term: url.searchParams.get("utm_term") || "",
    gclid: url.searchParams.get("gclid") || "",
    gbraid: url.searchParams.get("gbraid") || "",
    wbraid: url.searchParams.get("wbraid") || ""
  };

  if (Object.values(fromUrl).some(Boolean)) {
    const normalized = {
      ...fromUrl,
      source: fromUrl.source || (fromUrl.gclid || fromUrl.gbraid || fromUrl.wbraid ? "google" : ""),
      medium: fromUrl.medium || (fromUrl.gclid || fromUrl.gbraid || fromUrl.wbraid ? "cpc" : "")
    };
    writeStoredAttribution(normalized);
    return normalized;
  }

  const stored = readStoredAttribution();
  if (stored && Object.values(stored).some(Boolean)) return stored;

  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname !== window.location.hostname) {
        return { ...emptyAttribution, source: referrer.hostname, medium: "referral" };
      }
    } catch {
      /* pomiń nieczytelny referrer */
    }
  }

  return emptyAttribution;
}

export function getAttributionSource(context = "") {
  if (typeof window === "undefined") return context || "ochro.pl";

  const url = new URL(window.location.href);
  const base = context || `${url.pathname || "/"}${url.hash || ""}`;
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "gbraid", "wbraid", "fbclid"];
  const params = keys
    .map((key) => {
      const value = url.searchParams.get(key);
      return value ? `${key}=${value.slice(0, 80)}` : "";
    })
    .filter(Boolean);

  return params.length ? `${base}?${params.join("&")}`.slice(0, 240) : base.slice(0, 240);
}

function deviceType() {
  const width = window.innerWidth || 0;
  if (width < 768) return "mobile";
  if (width < 1100) return "tablet";
  return "desktop";
}

function stringParam(value: AnalyticsParams[string]) {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : "";
}

function numericParam(value: AnalyticsParams[string]) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function appendScript(id: string, src: string) {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function ensureGoogleCommand(win: AnalyticsWindow) {
  win.dataLayer = win.dataLayer || [];
  win.gtag =
    win.gtag ||
    function gtag(...args: unknown[]) {
      win.dataLayer?.push(args);
    };
}

function googleConsentState(win: AnalyticsWindow) {
  ensureGoogleCommand(win);
  if (!win.ochroGoogleConsentDefaulted) {
    win.gtag?.("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      functionality_storage: "granted",
      security_storage: "granted"
    });
    win.ochroGoogleConsentDefaulted = true;
  }

  win.gtag?.("consent", "update", {
    analytics_storage: hasAnalyticsConsent() ? "granted" : "denied",
    ad_storage: hasMarketingConsent() ? "granted" : "denied",
    ad_user_data: hasMarketingConsent() ? "granted" : "denied",
    ad_personalization: hasMarketingConsent() ? "granted" : "denied"
  });
}

function configureGoogleDestinations(win: AnalyticsWindow) {
  if (GA_MEASUREMENT_ID && hasAnalyticsConsent() && !win.ochroGoogleAnalyticsConfigured) {
    win.gtag?.("config", GA_MEASUREMENT_ID, { send_page_view: false });
    win.ochroGoogleAnalyticsConfigured = true;
  }
  if (GOOGLE_ADS_CONVERSION_ID && hasMarketingConsent() && !win.ochroGoogleAdsConfigured) {
    win.gtag?.("config", GOOGLE_ADS_CONVERSION_ID, { send_page_view: false });
    win.ochroGoogleAdsConfigured = true;
  }
}

function ensureGoogleTag(win: AnalyticsWindow) {
  if (!GOOGLE_TAG_ID) return;

  googleConsentState(win);
  if (win.ochroAnalyticsLoaded) {
    configureGoogleDestinations(win);
    return;
  }

  win.gtag?.("js", new Date());
  appendScript("ochro-google-tag", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_TAG_ID)}`);
  configureGoogleDestinations(win);
  win.ochroAnalyticsLoaded = true;
}

function ensureMetaPixel(win: AnalyticsWindow) {
  if (!META_PIXEL_ID || win.ochroMarketingLoaded) return;

  win.fbq =
    win.fbq ||
    function fbq(...args: unknown[]) {
      const queue = (win.fbq as unknown as { queue?: unknown[] }).queue || [];
      queue.push(args);
      (win.fbq as unknown as { queue?: unknown[] }).queue = queue;
    };
  win._fbq = win.fbq;
  (win.fbq as unknown as { loaded?: boolean; version?: string; queue?: unknown[] }).loaded = true;
  (win.fbq as unknown as { loaded?: boolean; version?: string; queue?: unknown[] }).version = "2.0";
  (win.fbq as unknown as { loaded?: boolean; version?: string; queue?: unknown[] }).queue =
    (win.fbq as unknown as { queue?: unknown[] }).queue || [];
  win.fbq("init", META_PIXEL_ID);
  appendScript("ochro-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  win.ochroMarketingLoaded = true;
}

function externalEventParams(params: AnalyticsParams) {
  const value = numericParam(params.eventValue ?? params.amount);
  return {
    product_id: stringParam(params.product),
    calculator_id: stringParam(params.calculator || params.product),
    source_area: stringParam(params.source),
    ...(value === undefined ? {} : { value, currency: "PLN" })
  };
}

function sendGoogleEvent(win: AnalyticsWindow, name: string, params: AnalyticsParams) {
  if (!GA_MEASUREMENT_ID) return;
  ensureGoogleTag(win);
  if (!win.gtag) return;

  win.gtag("event", name, externalEventParams(params));
}

function sendGoogleAdsLeadConversion(win: AnalyticsWindow, params: AnalyticsParams) {
  if (!GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_LEAD_LABEL) return;
  ensureGoogleTag(win);
  if (!win.gtag) return;

  win.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`,
    ...externalEventParams(params)
  });
}

function sendMetaEvent(win: AnalyticsWindow, name: string, params: AnalyticsParams) {
  ensureMetaPixel(win);
  if (!win.fbq) return;

  const eventParams = externalEventParams(params);
  if (name === "lead_success") {
    win.fbq("track", "Lead", eventParams);
    return;
  }
  if (name === "telefon_click") {
    win.fbq("track", "Contact", eventParams);
    return;
  }
  if (name === "page_view") {
    win.fbq("track", "PageView");
    return;
  }
  if (name === "oblicz_skladke") {
    win.fbq("trackCustom", "CalculatorResult", eventParams);
    return;
  }
  if (name === "formularz_start") {
    win.fbq("trackCustom", "FormStart", eventParams);
  }
}

export function syncTrackingIntegrations() {
  if (typeof window === "undefined") return;

  const win = window as AnalyticsWindow;
  if (GOOGLE_TAG_ID) {
    ensureGoogleTag(win);
  }
  if (hasMarketingConsent()) {
    ensureMetaPixel(win);
  }
}

function sendFirstPartyEvent(name: string, params: AnalyticsParams) {
  const traffic = currentAttribution();
  const body = JSON.stringify({
    consent: true,
    name,
    sessionId: sessionId(),
    pagePath: window.location.pathname || "/",
    pageTitle: document.title,
    referrer: cleanUrl(document.referrer),
    source: stringParam(params.source) || traffic.source,
    medium: stringParam(params.medium) || traffic.medium,
    campaign: stringParam(params.campaign) || traffic.campaign,
    utmContent: traffic.content,
    utmTerm: traffic.term,
    gclid: traffic.gclid,
    gbraid: traffic.gbraid,
    wbraid: traffic.wbraid,
    product: stringParam(params.product),
    calculator: stringParam(params.calculator || params.product),
    eventValue: numericParam(params.eventValue ?? params.amount),
    device: deviceType(),
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight
  });

  if ("sendBeacon" in navigator) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon("/api/analytics", blob)) return;
  }

  void fetch("/api/analytics", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true
  }).catch(() => {
    /* analityka nie może przeszkadzać użytkownikowi */
  });
}

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  if (typeof window === "undefined") return;

  const win = window as AnalyticsWindow;
  const detail = { name, ...params };
  window.dispatchEvent(new CustomEvent("ochro:analytics", { detail }));

  if (hasAnalyticsConsent()) {
    sendGoogleEvent(win, name, params);
    win.dataLayer?.push({ event: name, ...params });
    win.plausible?.(name, { props: params });
    sendFirstPartyEvent(name, params);
  }

  if (hasMarketingConsent()) {
    sendMetaEvent(win, name, params);
    if (name === "lead_success") {
      sendGoogleAdsLeadConversion(win, params);
    }
  }
}
